import { useEffect, useState } from "react";
import {
  CalendarRange,
  CheckCircle2,
  Info,
  Lock,
  ShieldAlert,
  Sparkles,
  Unlock,
  Wallet,
} from "lucide-react";
import { applyWfFreeze, fetchWfFreezes, releaseWfFreeze } from "../../../Moduels/attendance/wfApi";
import DatePickerField from "../../common/DatePickerField";

const LEVELS = [
  {
    value: "attendance",
    label: "Attendance lock",
    short: "Attendance",
    icon: CalendarRange,
    tone: "sky",
    why: "Stops day-level edits (Present / Absent / Leave) for the selected dates.",
    useWhen: "Use after the month is verified and before payroll starts.",
  },
  {
    value: "payroll",
    label: "Payroll lock",
    short: "Payroll",
    icon: Wallet,
    tone: "amber",
    why: "Protects attendance that payroll already used, so totals cannot drift.",
    useWhen: "Use when a payroll run is in progress or already processed.",
  },
  {
    value: "financial",
    label: "Financial lock",
    short: "Financial",
    icon: ShieldAlert,
    tone: "rose",
    why: "Highest freeze. Blocks attendance and payroll changes for the period.",
    useWhen: "Use after books are closed or statutory filings are complete.",
  },
];

const TONE = {
  sky: {
    card: "border-sky-200 bg-gradient-to-br from-sky-50 to-white",
    active: "border-sky-500 ring-4 ring-sky-100 shadow-lg shadow-sky-200/60",
    badge: "bg-sky-100 text-sky-800",
    icon: "from-sky-500 to-blue-600",
    btn: "from-sky-600 to-indigo-600",
  },
  amber: {
    card: "border-amber-200 bg-gradient-to-br from-amber-50 to-white",
    active: "border-amber-500 ring-4 ring-amber-100 shadow-lg shadow-amber-200/60",
    badge: "bg-amber-100 text-amber-900",
    icon: "from-amber-500 to-orange-600",
    btn: "from-amber-600 to-orange-600",
  },
  rose: {
    card: "border-rose-200 bg-gradient-to-br from-rose-50 to-white",
    active: "border-rose-500 ring-4 ring-rose-100 shadow-lg shadow-rose-200/60",
    badge: "bg-rose-100 text-rose-800",
    icon: "from-rose-500 to-red-600",
    btn: "from-rose-600 to-red-600",
  },
};

function errText(ex) {
  const detail = ex?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return ex?.message || "Could not apply lock.";
}

export default function WfFreeze({ embedded = false }) {
  const [form, setForm] = useState({
    freeze_level: "attendance",
    range_start: "",
    range_end: "",
    notes: "",
  });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [freezes, setFreezes] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const selected = LEVELS.find((l) => l.value === form.freeze_level) || LEVELS[0];
  const tone = TONE[selected.tone];

  const loadFreezes = async () => {
    try {
      const data = await fetchWfFreezes();
      setFreezes(Array.isArray(data) ? data : data?.items ?? []);
    } catch {
      setFreezes([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadFreezes();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg("");
    setErr("");
    if (!form.range_start || !form.range_end) {
      setErr("Choose both from and to dates so the lock period is clear.");
      return;
    }
    setSaving(true);
    try {
      await applyWfFreeze({
        freeze_level: form.freeze_level,
        range_start: form.range_start,
        range_end: form.range_end,
        notes: form.notes || undefined,
      });
      setMsg(`${selected.label} applied for ${form.range_start} to ${form.range_end}.`);
      setForm((prev) => ({ ...prev, notes: "" }));
      await loadFreezes();
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setSaving(false);
    }
  };

  const onRelease = async (freezeId) => {
    if (!window.confirm("Release this attendance lock? Day edits will be allowed again for that period.")) return;
    setMsg("");
    setErr("");
    try {
      await releaseWfFreeze(freezeId);
      setMsg("Lock released. Attendance can be edited again for that range.");
      await loadFreezes();
    } catch (ex) {
      setErr(errText(ex));
    }
  };

  return (
    <div className={embedded ? "att-lock-embed" : "p-4 md:p-6 max-w-5xl mx-auto"}>
      {!embedded && (
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-slate-900">Lock Attendance</h1>
          <p className="text-sm text-slate-500 mt-1">
            Freeze a date range so payroll can run on a stable attendance month.
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-3 mb-5">
        {[
          {
            n: "1",
            title: "Finish the calendar first",
            text: "Mark Present, Absent, Leave, holidays and week-offs for the month you want to protect.",
          },
          {
            n: "2",
            title: "Choose lock level and dates",
            text: "Attendance lock is the usual choice. Payroll and financial locks are stricter and used later in the cycle.",
          },
          {
            n: "3",
            title: "Apply, then run payroll",
            text: "Active locks appear on the right. Release only if HR needs to correct a day and then lock again.",
          },
        ].map((step) => (
          <div key={step.n} className="rounded-2xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-6 h-6 rounded-lg bg-gradient-to-br from-slate-600 to-blue-700 text-white text-[11px] font-extrabold flex items-center justify-center">
                {step.n}
              </span>
              <p className="text-[13px] font-bold text-slate-900">{step.title}</p>
            </div>
            <p className="text-[12px] text-slate-500 leading-relaxed">{step.text}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] gap-5 items-start">
        <form onSubmit={onSubmit} className="rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50 overflow-hidden">
          <div className={`h-1.5 bg-gradient-to-r ${tone.btn}`} />
          <div className="p-5 md:p-6 space-y-5">
            <div>
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 mb-2">
                What should be locked?
              </p>
              <div className="grid sm:grid-cols-3 gap-2.5">
                {LEVELS.map((level) => {
                  const Icon = level.icon;
                  const active = form.freeze_level === level.value;
                  const t = TONE[level.tone];
                  return (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setForm({ ...form, freeze_level: level.value })}
                      className={`text-left rounded-2xl border p-3 transition-all duration-200 ${t.card} ${
                        active ? t.active : "hover:-translate-y-0.5 hover:shadow-md"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${t.icon} text-white flex items-center justify-center mb-2`}>
                        <Icon size={15} />
                      </div>
                      <p className="text-[13px] font-bold text-slate-900">{level.short}</p>
                      <p className="text-[11px] text-slate-500 mt-1 leading-snug">{level.why}</p>
                    </button>
                  );
                })}
              </div>
              <p className="mt-2.5 text-[12px] text-slate-600 flex items-start gap-1.5">
                <Info size={13} className="mt-0.5 shrink-0 text-slate-400" />
                {selected.useWhen}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <DatePickerField
                label="From date"
                required
                value={form.range_start}
                onChange={(date) => setForm({ ...form, range_start: date })}
                max={form.range_end || undefined}
              />
              <DatePickerField
                label="To date"
                required
                value={form.range_end}
                onChange={(date) => setForm({ ...form, range_end: date })}
                min={form.range_start || undefined}
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
                Notes <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <textarea
                className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:border-blue-400 focus:ring-4 focus:ring-blue-100 outline-none transition resize-none"
                placeholder="Example: March 2026 attendance verified — lock before payroll."
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            {msg && (
              <div className="flex items-start gap-2 rounded-xl border-l-4 border-emerald-500 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                <span className="font-semibold">{msg}</span>
              </div>
            )}
            {err && (
              <div className="flex items-start gap-2 rounded-xl border-l-4 border-rose-500 bg-rose-50 px-3 py-2.5 text-sm text-rose-800">
                <ShieldAlert size={16} className="mt-0.5 shrink-0" />
                <span className="font-semibold">{err}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white shadow-md bg-gradient-to-r ${tone.btn} hover:shadow-xl hover:-translate-y-0.5 transition disabled:opacity-60 disabled:hover:translate-y-0`}
            >
              <Lock size={15} />
              {saving ? "Applying lock…" : `Apply ${selected.short.toLowerCase()} lock`}
            </button>
          </div>
        </form>

        <aside className="rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/40 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center gap-2">
              <Sparkles size={15} className="text-blue-500" />
              <h3 className="text-sm font-bold text-slate-900">Active locks</h3>
            </div>
            <p className="text-[12px] text-slate-500 mt-1">
              These periods are currently frozen. Release only when a correction is approved.
            </p>
          </div>
          <div className="p-4 space-y-2.5 max-h-[28rem] overflow-y-auto">
            {loadingList && <p className="text-sm text-slate-400 px-1">Loading locks…</p>}
            {!loadingList && freezes.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center">
                <Lock size={18} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm font-semibold text-slate-600">No active locks</p>
                <p className="text-[12px] text-slate-400 mt-1">
                  Apply a lock on the left after the month calendar is complete.
                </p>
              </div>
            )}
            {freezes.map((f) => {
              const meta = LEVELS.find((l) => l.value === f.freeze_level) || LEVELS[0];
              const t = TONE[meta.tone];
              return (
                <div key={f.freeze_id} className="rounded-2xl border border-slate-200 bg-slate-50/80 px-3.5 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide ${t.badge}`}>
                      {meta.short}
                    </span>
                    <button
                      type="button"
                      onClick={() => onRelease(f.freeze_id)}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-600 hover:text-slate-800"
                    >
                      <Unlock size={12} />
                      Release
                    </button>
                  </div>
                  <p className="text-[13px] font-semibold text-slate-800 mt-1.5">
                    {f.range_start && f.range_end ? `${f.range_start} → ${f.range_end}` : "All dates"}
                  </p>
                  {f.notes ? <p className="text-[11px] text-slate-500 mt-0.5">{f.notes}</p> : null}
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
}
